import { Controller } from "@hotwired/stimulus";

/**
 * Admin Push Subscription controller for managing Web Push notification subscriptions.
 *
 * Usage:
 *   <div data-controller="admin-push-subscription"
 *        data-admin-push-subscription-vapid-public-key-value="<%= vapid_public_key %>">
 *     <p data-admin-push-subscription-target="status">Checking...</p>
 *     <button data-admin-push-subscription-target="subscribeButton"
 *             data-action="admin-push-subscription#subscribe">Subscribe</button>
 *     <button data-admin-push-subscription-target="unsubscribeButton"
 *             data-action="admin-push-subscription#unsubscribe" class="hidden">Unsubscribe</button>
 *   </div>
 */
export default class AdminPushSubscriptionController extends Controller {
  static values = {
    vapidPublicKey: String,
  };

  static targets = ["status", "subscribeButton", "unsubscribeButton", "iosWarning"];

  declare vapidPublicKeyValue: string;
  declare statusTarget: HTMLElement;
  declare subscribeButtonTarget: HTMLButtonElement;
  declare unsubscribeButtonTarget: HTMLButtonElement;
  declare hasIosWarningTarget: boolean;
  declare iosWarningTarget: HTMLElement;

  private isSubscribed = false;

  connect(): void {
    this.checkSupport();
  }

  private async checkSupport(): Promise<void> {
    // Check basic support
    if (!("serviceWorker" in navigator)) {
      this.showUnsupported("Service Workers are not supported in this browser");
      return;
    }

    if (!("PushManager" in window)) {
      this.showUnsupported("Push notifications are not supported in this browser");
      return;
    }

    // iOS detection - show add to home screen warning
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    const isStandalone = window.matchMedia("(display-mode: standalone)").matches ||
                         (navigator as any).standalone === true;

    if (isIOS && !isStandalone && this.hasIosWarningTarget) {
      this.iosWarningTarget.classList.remove("hidden");
    }

    // Register service worker
    try {
      await navigator.serviceWorker.register("/service-worker");
      await this.checkSubscription();
    } catch (error) {
      console.error("Service worker registration failed:", error);
      this.showUnsupported("Failed to register service worker");
    }
  }

  private async checkSubscription(): Promise<void> {
    try {
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();

      this.isSubscribed = !!subscription;
      this.updateUI();
    } catch (error) {
      console.error("Failed to check subscription:", error);
      this.showStatus("Error checking subscription status", true);
    }
  }

  async subscribe(): Promise<void> {
    this.subscribeButtonTarget.disabled = true;
    this.showStatus("Requesting permission...");

    try {
      // Request notification permission
      const permission = await Notification.requestPermission();

      if (permission !== "granted") {
        this.showStatus("Notification permission denied. Please enable notifications in your browser settings.", true);
        this.subscribeButtonTarget.disabled = false;
        return;
      }

      this.showStatus("Subscribing to push notifications...");

      // Subscribe via Push API
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: this.urlBase64ToUint8Array(this.vapidPublicKeyValue),
      });

      // Send subscription to server
      const subscriptionJson = subscription.toJSON();
      const response = await fetch("/admin/push_subscription", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": this.csrfToken(),
        },
        body: JSON.stringify({
          push_subscription: {
            endpoint: subscriptionJson.endpoint,
            keys: subscriptionJson.keys,
          },
        }),
      });

      if (response.ok) {
        this.isSubscribed = true;
        this.updateUI();
        this.showStatus("Successfully subscribed to push notifications!");
      } else {
        throw new Error("Server rejected subscription");
      }
    } catch (error) {
      console.error("Failed to subscribe:", error);
      this.showStatus(`Failed to subscribe: ${error instanceof Error ? error.message : "Unknown error"}`, true);
    } finally {
      this.subscribeButtonTarget.disabled = false;
    }
  }

  async unsubscribe(): Promise<void> {
    this.unsubscribeButtonTarget.disabled = true;
    this.showStatus("Unsubscribing...");

    try {
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();

      if (subscription) {
        // Unsubscribe from browser
        await subscription.unsubscribe();

        // Remove from server
        await fetch("/admin/push_subscription", {
          method: "DELETE",
          headers: {
            "Content-Type": "application/json",
            "X-CSRF-Token": this.csrfToken(),
          },
          body: JSON.stringify({ endpoint: subscription.endpoint }),
        });
      }

      this.isSubscribed = false;
      this.updateUI();
      this.showStatus("Successfully unsubscribed from push notifications");
    } catch (error) {
      console.error("Failed to unsubscribe:", error);
      this.showStatus(`Failed to unsubscribe: ${error instanceof Error ? error.message : "Unknown error"}`, true);
    } finally {
      this.unsubscribeButtonTarget.disabled = false;
    }
  }

  private updateUI(): void {
    if (this.isSubscribed) {
      this.subscribeButtonTarget.classList.add("hidden");
      this.unsubscribeButtonTarget.classList.remove("hidden");
      this.showStatus("You are subscribed to push notifications");
    } else {
      this.subscribeButtonTarget.classList.remove("hidden");
      this.unsubscribeButtonTarget.classList.add("hidden");
      this.showStatus("Not subscribed to push notifications");
    }
  }

  private showUnsupported(message: string): void {
    this.statusTarget.textContent = message;
    this.statusTarget.classList.add("text-warning");
    this.subscribeButtonTarget.disabled = true;
    this.subscribeButtonTarget.classList.add("btn-disabled");
  }

  private showStatus(message: string, isError = false): void {
    this.statusTarget.textContent = message;
    this.statusTarget.classList.remove("text-warning", "text-error", "text-success");

    if (isError) {
      this.statusTarget.classList.add("text-error");
    }
  }

  private csrfToken(): string {
    const meta = document.querySelector('meta[name="csrf-token"]');
    return meta?.getAttribute("content") || "";
  }

  private urlBase64ToUint8Array(base64String: string): Uint8Array {
    const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
    const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);

    for (let i = 0; i < rawData.length; ++i) {
      outputArray[i] = rawData.charCodeAt(i);
    }

    return outputArray;
  }
}
